import apiFetch from "@/src/utils/apiFetch";

import {
  openConfirmDialog,
  openDialog,
  openDrawer,
} from "@/src/components/dialogs";

import { Button, CornerIconButton } from "@/src/components/buttons";
import { Card } from "@/src/components/cards";
import { Copyable } from "@/src/components/copyableTextComponent";
import { FetchableList } from "@/src/components/fetchableLists";
import SearchBar from "@/src/components/searchBar";
import { Input } from "@/src/components/universalInput";
import {
  DocumentVariable,
  FieldsSection,
  ManualVariable,
  manualVariables,
  patientColumnsNames,
  StatisticsReport,
  VariableTemplate,
} from "@/src/data/allTypes";
import { getUserId, openToast, uid } from "@/src/utils/utils";
import { useState } from "react";
import { Tooltip } from "react-tooltip";
import { FiltersPicker } from "../statistics/statisticsPage";
import { VariableInputPicker } from "./variableInputPicker";

export function VariablesChoiceList({
  currentVariable,
  sections,
  allVariables,
  onSelect,
}: {
  currentVariable: DocumentVariable | null | undefined;
  sections: (FieldsSection | null | undefined)[];
  allVariables: DocumentVariable[];
  onSelect: (variable: DocumentVariable) => void;
}) {
  const [variable, setVariable] = useState<DocumentVariable | null | undefined>(
    currentVariable || null
  );
  const [searchKey, setSearchKey] = useState("");
  const [variables, setVariables] = useState<
    (DocumentVariable | null | undefined)[]
  >(allVariables || []);

  const userColumns = {
    firstName: "Prénom",
    lastName: "Nom",
    grade: "Grade",
    sex: "Sexe",
    email: "Email",
    address: "Adresse",
    phoneNumber: "Numéro de téléphone",
    service: "Service",
    speciality: "Spécialité",
  };

  return (
    <div className="c1 gap10">
      <SearchBar
        autoFocus={true}
        placeholder="Rechercher une variable..."
        value={searchKey}
        onChange={(newValue) => {
          setSearchKey(newValue || "");
        }}
      ></SearchBar>
      <FetchableList
        queryFn={() => ["a"]}
        queryKey={["VariablesChoiceList", searchKey]}
        emptyChild={
          searchKey && (
            <div className="center grid c1 gap10 p20">
              Aucune variable disponible
            </div>
          )
        }
      >
        {(data) => (
          <div>
            <div className="c1 gap30">
              <div className="grid c1 gap20">
                <div className="bold f16">Specific</div>
                <div className="c3 gap10">
                  <div
                    className="card"
                    onClick={(e) => {
                      e.stopPropagation();
                      let newVariable: DocumentVariable = {
                        id: uid(),
                        name: `Condition ${
                          variables.filter((a) => {
                            a?.conditionGroup != null;
                          }).length + 1
                        }`,
                        category: "documentVariable",
                        source: "condition",
                        fieldId: null,
                        sectionId: null,
                        value: null,
                        format: "text",
                        nullValue: "-",

                        conditionGroup: {
                          id: uid(),
                          name: "Condition",
                          category: "conditionGroup",
                          type: "and",
                          creator: getUserId(),

                          conditions: [
                            {
                              id: uid(),
                              category: "condition",
                              a: "",
                              b: "",
                              operator: "=",
                              creator: getUserId(),
                            },
                          ],
                        },

                        conditionReplacement: "Hello",
                        conditionElseReplacement: "World",
                        creator: getUserId(),
                      };
                      onSelect(newVariable);
                    }}
                  >
                    Condition
                  </div>
                  <div
                    className="card"
                    onClick={async (e) => {
                      e.stopPropagation();

                      let report: StatisticsReport = {
                        males: 0,
                        females: 0,
                        patients: [],
                        patientsDates: {},
                        appointments: [],
                        appointmentsDates: {},
                        patientGroups: {},
                        type: "clinicalData",
                        creator: getUserId(),
                        groups: [],
                      };

                      await openDrawer({
                        id: "statisticsFiltersDrawer",
                        title: "Filtres",
                        additionalClasses: ["big"],
                        trailing: (
                          <div>
                            <Input
                              name={"Nom du rapport"}
                              type="text"
                              value={report.title || ""}
                              onChange={(newValue) => {
                                report.title = newValue;
                              }}
                            ></Input>
                          </div>
                        ),
                        content: (
                          <div className="c1 gap10">
                            <FiltersPicker
                              report={report}
                              variableSections={sections}
                              current={report.groups}
                              onChange={(newValue) => {
                                if (!report) return;
                                report.groups = newValue;
                              }}
                            ></FiltersPicker>
                          </div>
                        ),
                        onConfirm: async () => {
                          let newVariable: DocumentVariable = {
                            id: uid(),
                            name: report.title || `Statistique ${uid()}`,
                            category: "documentVariable",
                            source: "statistics",
                            fieldId: null,
                            sectionId: null,
                            value: null,
                            format: "text",
                            nullValue: "-",
                            statisticsReport: report,
                            creator: getUserId(),
                          };

                          console.log("report", report);
                          onSelect(newVariable);
                        },
                      });
                    }}
                  >
                    Statistique
                  </div>
                  <div
                    className="card"
                    onClick={async (e) => {
                      e.stopPropagation();
                      let newVariable: DocumentVariable = {
                        id: uid(),
                        name: `Variable asynchrone complexe`,
                        category: "documentVariable",
                        source: "manualVariables",
                        fieldId: null,
                        sectionId: null,
                        value: null,
                        format: "text",
                        nullValue: "-",
                        creator: getUserId(),
                      };

                      await openDrawer({
                        id: "statisticsFiltersDrawer",
                        title: "Choisir une variable asynchrone complexe",
                        content: (
                          <div className="c1 gap10">
                            <Input
                              name={"Choisir la variable"}
                              type="chips"
                              value={newVariable.value}
                              options={Object.values(manualVariables).map(
                                (variable) => ({
                                  label: variable.label,
                                  value: variable.value,
                                })
                              )}
                              onChange={(newValue) => {
                                newVariable.name =
                                  manualVariables[
                                    newValue as ManualVariable
                                  ].label || "Variable spéciale";
                                newVariable.value = newValue;
                              }}
                            ></Input>
                          </div>
                        ),
                        onConfirm: async () => {
                          onSelect(newVariable);
                        },
                      });
                    }}
                  >
                    Variable spéciale
                  </div>
                  <div
                    className="card"
                    onClick={async (e) => {
                      e.stopPropagation();
                      let newVariable: DocumentVariable = {
                        id: uid(),
                        name: `Html`,
                        category: "documentVariable",
                        source: "simpleHtml",
                        fieldId: null,
                        sectionId: null,
                        value: "Here",
                        format: "text",
                        nullValue: "-",

                        creator: getUserId(),
                      };

                      await openDialog({
                        title: "Code HTML",
                        content: (
                          <div className="c1 gap10">
                            <Input
                              value={newVariable.name}
                              type="text"
                              name={"Nom de la variable"}
                              onChange={(newValue) => {
                                newVariable.name = newValue;
                              }}
                            ></Input>
                            <VariableInputPicker
                              name={"Code HTML"}
                              value={newVariable.value || ""}
                              sections={sections}
                              toolbarId={uid()}
                              onChange={(newValue) => {
                                newVariable.value = newValue;
                              }}
                            ></VariableInputPicker>
                          </div>
                        ),
                        onConfirm: () => {
                          onSelect(newVariable);
                        },
                      });
                    }}
                  >
                    Simple Html
                  </div>
                </div>

                {sections?.length != 0 && (
                  <>
                    <div className="bold f16">Formulaire</div>
                    <div className="c1 gap20">
                      {sections?.map((section, index) => {
                        if (!section) return <></>;
                        let fields = section.fields?.filter((field) => {
                          if (
                            searchKey &&
                            !field?.name
                              ?.toLowerCase()
                              .includes(searchKey.toLowerCase())
                          )
                            return false;
                          return true;
                        });

                        if (!fields?.length) return <></>;

                        let sectionExists =
                          variables?.find(
                            (variable) =>
                              variable?.sectionId == section.id &&
                              variable?.source == "section"
                          ) != null;

                        return (
                          <div
                            key={uid()}
                            className="card grid c1 gap15 p10"
                            onClick={(e) => {
                              e.stopPropagation();
                              let newVariable: DocumentVariable = {
                                id: uid(),
                                name: `SECTION ${section.name}`,
                                category: "documentVariable",
                                source: "section",
                                fieldId: null,
                                sectionId: section.id,
                                value: null,
                                format: "text",
                                nullValue: "-",
                                creator: getUserId(),
                              };
                              onSelect(newVariable);
                            }}
                          >
                            <div
                              className={`bold f16 ${
                                sectionExists ? "blue-color" : ""
                              }`}
                            >
                              {section.name}
                            </div>

                            <div className="c2 gap15">
                              {fields?.map((field, index) => {
                                if (!field) return <></>;
                                let titleExists =
                                  variables?.find(
                                    (variable) =>
                                      variable?.fieldId == field.id &&
                                      variable?.source == "fieldTitle"
                                  ) != null;

                                let valueExists =
                                  variables?.find(
                                    (variable) =>
                                      variable?.fieldId == field.id &&
                                      variable?.source == "fieldValue"
                                  ) != null;

                                let isCurrentValue =
                                  variable?.fieldId == field.id &&
                                  variable?.source == "fieldValue";

                                let isCurrentTitle =
                                  variable?.fieldId == field.id &&
                                  variable?.source == "fieldTitle";

                                return (
                                  <div
                                    key={uid()}
                                    className={`c1 card gap10 acenter`}
                                    style={{
                                      background: "rgba(0, 164, 255, 0.15)",
                                      boxShadow: "none",
                                    }}
                                  >
                                    <div
                                      className={`${
                                        titleExists && valueExists
                                          ? "blue-color bold"
                                          : ""
                                      }`}
                                    >
                                      {field?.name}
                                    </div>
                                    <div className="c2 gap10">
                                      <div
                                        className={`card ${
                                          titleExists ? "blue-color bold" : ""
                                        }`}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          let variable: DocumentVariable = {
                                            id: uid(),
                                            category: "documentVariable",
                                            name: `Titre de ${field.name}`,
                                            source: "fieldTitle",
                                            fieldId: field.id,
                                            sectionId: null,
                                            value: null,
                                            format: "text",
                                            nullValue: "-",
                                            creator: getUserId(),
                                          };
                                          onSelect(variable);
                                        }}
                                      >
                                        Titre
                                      </div>
                                      <Tooltip
                                        id={`${field.id}-tooltip`}
                                        place="bottom"
                                        variant="info"
                                        content={field.options
                                          ?.map((option) => option?.name)
                                          ?.join(", ")}
                                      />
                                      <div
                                        data-tooltip-id={`${field.id}-tooltip`}
                                        className={`card ${
                                          isCurrentValue
                                            ? "blue bold"
                                            : valueExists
                                            ? "blue-color bold"
                                            : ""
                                        }`}
                                        onClick={async (e) => {
                                          e.stopPropagation();
                                          let variable: DocumentVariable = {
                                            id: uid(),
                                            category: "documentVariable",
                                            name: `${field.name}`,
                                            source: "fieldValue",
                                            fieldId: field.id,
                                            format: "text",
                                            sectionId: null,
                                            value: null,
                                            nullValue: "-",
                                            creator: getUserId(),
                                          };
                                          onSelect(variable);
                                        }}
                                      >
                                        Valeur{" "}
                                        <CornerIconButton
                                          type="more"
                                          onClick={async (e) => {
                                            await openDialog({
                                              id: "optionsDialog",
                                              title: field.name,
                                              content: (
                                                <div className="c1 gap10">
                                                  {field.options?.map(
                                                    (option) => {
                                                      return (
                                                        <div
                                                          key={uid()}
                                                          className="card"
                                                        >
                                                          <Copyable
                                                            str={
                                                              option?.name || ""
                                                            }
                                                          />
                                                        </div>
                                                      );
                                                    }
                                                  )}
                                                </div>
                                              ),
                                            });
                                          }}
                                        ></CornerIconButton>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>

              <div className="grid c1 gap10 ">
                <div className="bold f16">BASES</div>
                <div className="c2 gap10">
                  {Object.entries({
                    _date: "Date de création du document",
                    _modifiedDate: "Date de modification du document",
                    registryNumber: "Code sur le registre",
                  })?.map(([columnId, columnName]) => {
                    return (
                      <div
                        key={uid()}
                        className="card"
                        onClick={() => {
                          let variable: DocumentVariable = {
                            id: uid(),
                            category: "documentVariable",
                            name: columnName,
                            source: "patientDocument",
                            fieldId: null,
                            sectionId: null,
                            value: columnId,
                            format: "LL",
                            nullValue: "-",
                            creator: getUserId(),
                          };
                          onSelect(variable);
                        }}
                      >
                        {columnName}
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="grid c1 gap10 ">
                <div className="bold f16">PATIENT</div>
                <div className="c2 mc2 gap10">
                  {Object.entries(patientColumnsNames)?.map(
                    ([columnId, columnName]) => {
                      if (
                        searchKey &&
                        !columnName
                          ?.toLowerCase()
                          .includes(searchKey.toLowerCase())
                      )
                        return;

                      let exists =
                        variables?.find(
                          (variable) =>
                            variable?.value == columnId &&
                            variable?.source == "patient"
                        ) != null;

                      return (
                        <div
                          key={uid()}
                          className={`card ${exists ? "blue-color bold" : ""}`}
                          onClick={() => {
                            let variable: DocumentVariable = {
                              id: uid(),
                              category: "documentVariable",
                              name: `Patient ${columnName}`,
                              source: "patient",
                              fieldId: null,
                              sectionId: null,
                              value: columnId,
                              format: "text",
                              nullValue: "-",
                              creator: getUserId(),
                            };
                            onSelect(variable);
                          }}
                        >
                          {columnName}
                        </div>
                      );
                    }
                  )}
                </div>
              </div>

              <div className="grid c1 gap10">
                <div className="bold f16">USER</div>
                <div className="c4 mc1 gap10">
                  {Object.entries(userColumns)?.map(
                    ([columnId, columnName]) => {
                      if (
                        searchKey &&
                        !columnName
                          ?.toLowerCase()
                          .includes(searchKey.toLowerCase())
                      )
                        return;
                      let exists =
                        variables?.find(
                          (variable) =>
                            variable?.value == columnId &&
                            variable?.source == "user"
                        ) != null;
                      return (
                        <div
                          key={uid()}
                          className={`card ${exists ? "blue-color bold" : ""}`}
                          onClick={() => {
                            let variable: DocumentVariable = {
                              id: uid(),
                              category: "documentVariable",
                              name: `User ${columnName}`,
                              source: "user",
                              fieldId: null,
                              sectionId: null,
                              value: columnId,
                              format: "text",
                              nullValue: "-",
                              creator: getUserId(),
                            };
                            onSelect(variable);
                          }}
                        >
                          {columnName}
                        </div>
                      );
                    }
                  )}
                </div>
              </div>
            </div>

            <div className="grid c1 gap10">
              <div className="bold f16 mtop20">Custom Variables</div>
              <VariableTemplatesList
                searchKey={searchKey}
                sections={sections}
                onSelect={(variableTemplate) => {
                  let variable: DocumentVariable = {
                    id: uid(),
                    category: "documentVariable",
                    name: variableTemplate.name,
                    source: "simpleHtml",
                    fieldId: null,
                    sectionId: null,
                    value: variableTemplate.content,
                    format: "text",
                    nullValue: "-",
                    creator: getUserId(),
                  };
                  onSelect(variable);
                }}
              ></VariableTemplatesList>
            </div>
          </div>
        )}
      </FetchableList>
    </div>
  );
}

function VariableTemplatesList({
  sections,
  searchKey,
  onSelect,
}: {
  sections: (FieldsSection | null | undefined)[] | null | undefined;
  searchKey: string | null | undefined;
  onSelect: (variableTemplate: VariableTemplate) => void;
}) {
  const [reload, setReload] = useState(0);

  async function openVariableTemplateDrawer(
    variableTemplate: VariableTemplate
  ) {
    await openDialog({
      id: "saveTemplateDialog",
      title: "Enregistrer comme modèle",
      content: (
        <div className="c1 gap10">
          <Input
            value={variableTemplate.name}
            type="text"
            isRequired={true}
            placeholder="Nom du modèle"
            name={"Nom du modèle"}
            onChange={(newValue) => {
              variableTemplate.name = newValue;
            }}
          ></Input>
          <VariableInputPicker
            name={"Code HTML"}
            value={variableTemplate.content || ""}
            sections={sections || []}
            toolbarId={uid()}
            onChange={(newValue) => {
              variableTemplate.content = newValue;
            }}
          ></VariableInputPicker>
        </div>
      ),
      onConfirm: async () => {
        await apiFetch("variableTemplates", "addOne", {
          variableTemplate,
        });
        setReload((prev) => prev + 1);
        openToast("success", "Modèle enregistré avec succès");
      },
    });
  }

  return (
    <div className="c1 gap10">
      <FetchableList
        queryKey={["VariableTemplatesList", searchKey, reload]}
        queryFn={() =>
          apiFetch("variableTemplates", "loadMany", {
            creatorId: getUserId(),
            searchKey,
          })
        }
      >
        {(data) => {
          data?.map((variableTemplate: VariableTemplate) => {
            return (
              <Card
                key={uid()}
                color="simple"
                onClick={() => {
                  onSelect(variableTemplate);
                }}
                onEdit={async () => {
                  await openVariableTemplateDrawer(variableTemplate);
                }}
                onDelete={() => {
                  openConfirmDialog({
                    id: "deleteTemplateDialog",
                    title: "Supprimer le modèle",
                    content: "Êtes-vous sûr de vouloir supprimer ce modèle?",
                    onConfirm: async () => {
                      await apiFetch("general", "deleteAny", {
                        id: variableTemplate.id,
                        table: "variableTemplates",
                      });
                      setReload((prev) => prev + 1);
                      openToast("success", "Modèle supprimé avec succès");
                    },
                  });
                }}
              >
                {variableTemplate.name}
              </Card>
            );
          });
        }}
      </FetchableList>
      <Button
        type={"tertiary"}
        onClick={async () => {
          let variableTemplate: VariableTemplate = {
            name: `Variable`,
            category: "standard",
            content: "",
            creator: getUserId(),
          };
          await openVariableTemplateDrawer(variableTemplate);
        }}
      >
        Créer un nouveau modèle de variable
      </Button>
    </div>
  );
}
